import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { Calendar, X } from 'lucide-react';

const TimelineFullscreen = ({
  isOpen,
  onClose,
  selectedDate,
  projectPeople,
  projectCars,
  timelineStartTime,
  timelineEndTime,
  setTimelineStartTime,
  setTimelineEndTime,
  turbineAssignments,
  vehicleAssignments,
  getAllAssignments,
  getIndividualTimes,
  isPersonAssignedAtTime,
  isCarAssignedAtTime,
  getCarAssignmentAtTime,
  handleEditAssignment,
  handleRemovePersonFromTaskAtTime,
  handleTaskDrop,
  handleTimeSlotDragOver,
  handleTaskMoveStart,
  groupTimeSlotsByAssignment,
  groupCarTimeSlotsByAssignment,
  timeToMinutes,
  draggedTask,
  setDraggedTask,
  dropZone,
  setDropZone,
  dragMode,
  setDragMode,
  resizeHandle,
  setResizeHandle,
  dragStartX,
  setDragStartX
}) => {
  // Zoom level state
  const [zoomLevel, setZoomLevel] = useState(80); // percentage - start at 80% for better fit

  // Calculate cell dimensions based on zoom
  const cellWidth = Math.round(60 * (zoomLevel / 100));
  const cellHeight = Math.round(50 * (zoomLevel / 100));
  const headerCellHeight = Math.round(40 * (zoomLevel / 100));
  const badgeSize = Math.round(7 * (zoomLevel / 100));
  // Generate time slots based on start and end time
  const timeSlots = useMemo(() => {
    const slots = [];
    const startMinutes = timeToMinutes(timelineStartTime);
    let endMinutes = timeToMinutes(timelineEndTime);

    // Handle overnight shifts
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60; // Add 24 hours for overnight
    }

    for (let minutes = startMinutes; minutes <= endMinutes; minutes += 15) {
      const displayMinutes = minutes % (24 * 60);
      const hours = Math.floor(displayMinutes / 60);
      const mins = displayMinutes % 60;
      const time = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      slots.push(time);
    }

    return slots;
  }, [timelineStartTime, timelineEndTime, timeToMinutes]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-hidden flex flex-col">
      {/* Fullscreen Header */}
      <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Calendar className="w-6 h-6 mr-3 text-green-600" />
              Daily Schedule Timeline - Fullscreen View
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')} • {projectPeople.length} team members • Time range: {timelineStartTime} - {timelineEndTime}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            {/* Zoom Controls */}
            <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-lg">
              <button
                onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Zoom out"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-[50px] text-center">
                {zoomLevel}%
              </span>
              <button
                onClick={() => setZoomLevel(Math.min(150, zoomLevel + 10))}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Zoom in"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={() => setZoomLevel(100)}
                className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors ml-2"
                title="Reset zoom"
              >
                Reset
              </button>
            </div>

            {/* Time Range Controls */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Time Range:</label>
              <input
                type="time"
                value={timelineStartTime}
                onChange={(e) => setTimelineStartTime(e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <span className="text-gray-500 text-sm">to</span>
              <input
                type="time"
                value={timelineEndTime}
                onChange={(e) => setTimelineEndTime(e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Quick Presets */}
            <div className="flex space-x-1">
              <button
                onClick={() => {
                  setTimelineStartTime('07:00');
                  setTimelineEndTime('19:00');
                }}
                className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                Day Shift
              </button>
              <button
                onClick={() => {
                  setTimelineStartTime('19:00');
                  setTimelineEndTime('07:00');
                }}
                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                Night Shift
              </button>
              <button
                onClick={() => {
                  setTimelineStartTime('00:00');
                  setTimelineEndTime('23:45');
                }}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Full Day
              </button>
            </div>

            {/* Exit Fullscreen */}
            <button
              onClick={onClose}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              title="Exit fullscreen (ESC)"
            >
              <X className="w-4 h-4" />
              <span className="text-sm font-medium">Exit Fullscreen</span>
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen Timeline Content */}
      <div className="flex-1 bg-gray-50 p-6 overflow-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="text-sm text-gray-500">
              Showing: {timeSlots.length} time slots ({timelineStartTime} - {timelineEndTime})
              {timeToMinutes(timelineEndTime) < timeToMinutes(timelineStartTime) && (
                <span className="ml-2 text-blue-600">(Overnight shift)</span>
              )}
            </div>
          </div>

          {/* Timeline Grid - Enhanced for Fullscreen */}
          <div className="p-4">
            <div className="timeline-container overflow-x-auto overflow-y-visible" style={{ minHeight: `${headerCellHeight + 20 + (projectPeople.length + projectCars.length) * cellHeight + 60}px` }}>
              <div style={{ minWidth: `${200 + (timeSlots.length * cellWidth)}px` }}>
                {/* Timeline Header */}
                <div className="grid" style={{ gridTemplateColumns: `200px repeat(${timeSlots.length}, ${cellWidth}px)` }}>
                  <div className={`p-2 font-semibold text-gray-700 border-b-2 border-gray-300 sticky left-0 bg-white z-10 flex items-center w-[200px]`} style={{ height: `${headerCellHeight}px` }}>
                    Team Members
                  </div>
                  {timeSlots.map(time => (
                    <div key={time} className={`p-1 text-sm font-semibold text-center border-b-2 border-gray-300 flex items-center justify-center ${
                      time === '00:00' ? 'text-purple-700 bg-purple-50' : 'text-gray-700'
                    }`} style={{ height: `${headerCellHeight}px`, fontSize: `${12 * (zoomLevel / 100)}px` }}>
                      {time.substring(0, 5)}
                    </div>
                  ))}
                </div>

                {/* Timeline Rows for Each Person - Larger */}
                <div className="space-y-2 mt-4">
                  {projectPeople.map(person => (
                    <div key={person.id} className="flex hover:bg-gray-50 rounded-lg" data-person-id={person.id}>
                      {/* Person Info - Larger */}
                      <div className="p-2 flex items-center border-r-2 border-gray-200 sticky left-0 bg-white z-10" style={{ minWidth: '200px', width: '200px', height: `${cellHeight}px` }}>
                        <div>
                          <div className="font-medium text-gray-900" style={{ fontSize: `${13 * (zoomLevel / 100)}px` }}>
                            {person.first_name && person.last_name
                              ? `${person.first_name} ${person.last_name}`
                              : person.username}
                          </div>
                          {person.role && zoomLevel >= 80 && (
                            <div className="text-gray-500" style={{ fontSize: `${11 * (zoomLevel / 100)}px` }}>{person.role}</div>
                          )}
                        </div>
                      </div>

                      {/* Time Blocks Container - Larger */}
                      <div className="flex">
                        {/* Time Blocks - Grouped consecutive assignments */}
                        {groupTimeSlotsByAssignment(person.id, timeSlots).map((block, blockIndex) => {
                          const isAssigned = block.assignment !== null;
                          const assignment = block.assignment;
                          const blockWidth = isAssigned ? block.slotCount * cellWidth : cellWidth;

                          return (
                            <div
                              key={`${block.startTime}-${blockIndex}`}
                              className={`border border-gray-200 flex items-center justify-center relative group overflow-visible ${
                                isAssigned
                                  ? 'p-2 border-gray-400 hover:opacity-80 rounded-lg select-none transition-all duration-150 shadow-sm'
                                  : 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                              } ${
                                dropZone && dropZone.personId === person.id && dropZone.timeSlot === block.startTime
                                  ? 'bg-green-200 border-green-400 ring-2 ring-green-300 shadow-lg'
                                  : ''
                              } ${
                                isAssigned && dragMode === 'move'
                                  ? 'cursor-grabbing'
                                  : isAssigned
                                    ? 'cursor-grab'
                                    : ''
                              }`}
                              data-time-slot={block.startTime}
                              style={{
                                width: `${blockWidth}px`,
                                height: `${cellHeight}px`,
                                minHeight: `${cellHeight}px`,
                                backgroundColor: dropZone && dropZone.personId === person.id && dropZone.timeSlot === block.startTime
                                  ? '#BBF7D0'
                                  : isAssigned && assignment?.categoryColor
                                    ? `${assignment.categoryColor}30`
                                    : isAssigned
                                      ? '#DBEAFE'
                                      : '#F9FAFB',
                                border: dropZone && dropZone.personId === person.id && dropZone.timeSlot === block.startTime
                                  ? '2px solid #10B981'
                                  : isAssigned ? `2px solid ${assignment?.categoryColor || '#3B82F6'}` : '1px solid #e5e7eb',
                                borderRadius: isAssigned ? '12px' : undefined,
                                opacity: draggedTask && draggedTask.assignment && draggedTask.assignment.id === assignment?.id && draggedTask.personId === person.id ? 0.5 : 1,
                                transform: draggedTask && draggedTask.assignment && draggedTask.assignment.id === assignment?.id && draggedTask.personId === person.id ? 'scale(1.02)' : 'scale(1)'
                              }}
                              title={
                                assignment
                                  ? (() => {
                                      const { startTime, endTime } = getIndividualTimes(assignment, person.id);
                                      return `${assignment.turbineName}: ${assignment.category ? `${assignment.category} - ${assignment.subcategory}` : assignment.subcategory} (${startTime}-${endTime})\nDuration: ${block.slotCount * 15} minutes\nDouble-click to edit, drag to move, drag edges to resize`;
                                    })()
                                  : `Click to assign task at ${block.startTime}`
                              }
                              onDrop={(e) => {
                                if (!isAssigned) {
                                  handleTaskDrop(e, block.startTime);
                                }
                              }}
                              onDragOver={handleTimeSlotDragOver}
                              onMouseDown={(e) => {
                                if (assignment && e.button === 0) {
                                  handleTaskMoveStart(e, assignment, person.id);
                                }
                              }}
                              onDoubleClick={(e) => {
                                if (assignment) {
                                  handleEditAssignment(assignment, person.id);
                                }
                              }}
                            >
                              {assignment && (
                                <div className="w-full px-1 overflow-hidden flex items-center justify-center gap-1">
                                  {/* Turbine Badge */}
                                  <div
                                    className="rounded-full flex items-center justify-center text-white font-bold shadow-md flex-shrink-0"
                                    style={{
                                      backgroundColor: assignment.categoryColor || '#10B981',
                                      width: `${badgeSize}px`,
                                      height: `${badgeSize}px`,
                                      minWidth: `${badgeSize}px`,
                                      fontSize: `${10 * (zoomLevel / 100)}px`
                                    }}
                                  >
                                    T{assignment.turbineId}
                                  </div>
                                  {/* Task Description */}
                                  <div className="truncate font-medium whitespace-nowrap overflow-hidden" style={{
                                    color: assignment.categoryColor || '#1E40AF',
                                    fontSize: `${11 * (zoomLevel / 100)}px`
                                  }}>
                                    {assignment.category ? `${assignment.category} - ${assignment.subcategory}` : assignment.subcategory}
                                  </div>

                                  {/* Remove button - appears on hover */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemovePersonFromTaskAtTime(person.id, assignment, block.startTime);
                                    }}
                                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm hover:bg-red-600 shadow-md"
                                    title={`Remove ${person.first_name && person.last_name ? `${person.first_name} ${person.last_name}` : person.username} from this task`}
                                  >
                                    ×
                                  </button>

                                  {/* Left resize handle */}
                                  <div
                                    className="resize-handle absolute left-0 top-0 w-3 h-full bg-blue-500 opacity-0 group-hover:opacity-100 cursor-ew-resize transition-opacity z-20 rounded-l"
                                    title="Drag to change start time"
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setDragMode('resize');
                                      setResizeHandle('left');
                                      const { startTime: personStartTime, endTime: personEndTime } = getIndividualTimes(assignment, person.id);
                                      setDraggedTask({
                                        assignment: { ...assignment },
                                        personId: person.id,
                                        originalStartTime: personStartTime,
                                        originalEndTime: personEndTime
                                      });
                                      setDragStartX(e.clientX);
                                    }}
                                  />

                                  {/* Right resize handle */}
                                  <div
                                    className="resize-handle absolute right-0 top-0 w-3 h-full bg-blue-500 opacity-0 group-hover:opacity-100 cursor-ew-resize transition-opacity z-20 rounded-r"
                                    title="Drag to change end time"
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setDragMode('resize');
                                      setResizeHandle('right');
                                      const { startTime: personStartTime, endTime: personEndTime } = getIndividualTimes(assignment, person.id);
                                      setDraggedTask({
                                        assignment: { ...assignment },
                                        personId: person.id,
                                        originalStartTime: personStartTime,
                                        originalEndTime: personEndTime
                                      });
                                      setDragStartX(e.clientX);
                                    }}
                                  />
                                </div>
                              )}
                              {/* Empty slot indicator */}
                              {!assignment && (
                                <div className="w-full h-full flex items-center justify-center opacity-30">
                                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Cars Rows - under people */}
                  {projectCars.length > 0 && (
                    <>
                      {/* Separator */}
                      <div className="border-t-2 border-gray-400 my-4"></div>

                      {/* Cars Header */}
                      <div className="flex">
                        <div className="p-2 font-semibold text-gray-700 sticky left-0 bg-white z-10 h-[30px] flex items-center w-[200px]">
                          Vehicles
                        </div>
                      </div>

                      {projectCars.map(car => (
                        <div key={`car-${car.id}`} className="flex hover:bg-orange-50 rounded-lg">
                          {/* Car Info - Larger */}
                          <div className="p-2 flex items-center border-r-2 border-gray-200 sticky left-0 bg-white z-10" style={{ minWidth: '200px', width: '200px', height: `${cellHeight}px` }}>
                            <div>
                              <div className="font-medium text-gray-900" style={{ fontSize: `${13 * (zoomLevel / 100)}px` }}>
                                {car.car_brand} {car.car_model}
                              </div>
                              <div className="text-gray-500" style={{ fontSize: `${11 * (zoomLevel / 100)}px` }}>
                                {car.plate_number} • {car.number_of_seats} seats
                              </div>
                            </div>
                          </div>

                          {/* Time Blocks Container - Larger */}
                          <div className="flex">
                            {/* Time Blocks - Grouped consecutive car assignments */}
                            {groupCarTimeSlotsByAssignment(car.id, timeSlots).map((block, blockIndex) => {
                              const isAssigned = block.assignment !== null;
                              const assignment = block.assignment;
                              const blockWidth = isAssigned ? block.slotCount * cellWidth : cellWidth;

                              return (
                                <div
                                  key={`car-${block.startTime}-${blockIndex}`}
                                  className={`border border-gray-200 flex items-center justify-center relative group overflow-visible ${
                                    isAssigned
                                      ? 'p-2 border-orange-400 hover:opacity-80 rounded-lg select-none transition-all duration-150 shadow-sm'
                                      : 'bg-gray-50 hover:bg-orange-50 cursor-pointer'
                                  }`}
                                  data-time-slot={block.startTime}
                                  style={{
                                    width: `${blockWidth}px`,
                                    height: `${cellHeight}px`,
                                    minHeight: `${cellHeight}px`,
                                    backgroundColor: isAssigned && assignment?.categoryColor
                                      ? `${assignment.categoryColor}20`
                                      : isAssigned
                                        ? '#FED7AA'
                                        : '#F9FAFB',
                                    border: isAssigned ? `2px solid ${assignment?.categoryColor || '#EA580C'}` : '1px solid #e5e7eb',
                                    borderRadius: isAssigned ? '12px' : undefined
                                  }}
                                  title={
                                    assignment
                                      ? `T${assignment.turbineId}: ${assignment.subcategory}\n${assignment.startTime}-${assignment.endTime}\nCar assignment`
                                      : `${car.car_brand} ${car.car_model} - ${block.startTime}\nCar available`
                                  }
                                >
                                  {assignment && (
                                    <div className="w-full px-1 overflow-hidden flex items-center justify-center gap-1">
                                      {/* Turbine Badge for Car */}
                                      <div
                                        className="rounded-full flex items-center justify-center text-white font-bold shadow-md flex-shrink-0"
                                        style={{
                                          backgroundColor: assignment.categoryColor || '#EA580C',
                                          width: `${badgeSize}px`,
                                          height: `${badgeSize}px`,
                                          minWidth: `${badgeSize}px`,
                                          fontSize: `${10 * (zoomLevel / 100)}px`
                                        }}
                                      >
                                        T{assignment.turbineId}
                                      </div>
                                      {/* Task Description for Car */}
                                      <div className="truncate font-medium whitespace-nowrap overflow-hidden" style={{
                                        color: assignment.categoryColor || '#EA580C',
                                        fontSize: `${11 * (zoomLevel / 100)}px`
                                      }}>
                                        {assignment.subcategory}
                                      </div>
                                    </div>
                                  )}
                                  {/* Empty slot indicator */}
                                  {!assignment && (
                                    <div className="w-full h-full flex items-center justify-center opacity-30">
                                      <div className="w-2 h-2 bg-orange-300 rounded-full"></div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineFullscreen;